import {Intent} from "@blueprintjs/core";
import {action, computed, makeObservable, observable} from "mobx";

import {HttpError} from "../api/http-error";
import {QuickEditEntry} from "../components/organisms/publish-drawer/quick-edit-entry";
import {BigIntId} from "../utils/big-int-id";
import {PageRouter} from "../utils/route-util";

interface IHttpErrorDetail {
    intent: Intent;
    message: string;
}

export enum SimpleToast {
    SAVED = "SAVED",
    DELETED = "DELETED",
    FINISHED_PUBLISH_SETTING = "FINISHED_PUBLISH_SETTING",
    FINISHED_ARCHIVED_SETTING = "FINISHED_ARCHIVED_SETTING",
    CANNOT_OPEN_WINDOW = "CANNOT_OPEN_WINDOW",
    SCHEDULED_REVISION_EXISTS = "SCHEDULED_REVISION_EXISTS",
    COMPLETED = "COMPLETED",
    PROFILE_UPDATED = "PROFILE_UPDATED",
    PROFILE_UPDATE_FAILED = "PROFILE_UPDATE_FAILED",
    PROFILE_UPDATE_SAM_FAILED = "PROFILE_UPDATE_SAM_FAILED",
    PROFILE_FETCH_FAILED = "PROFILE_FETCH_FAILED",
    PASSWORD_CHANGE_COMPLETE = "PASSWORD_CHANGE_COMPLETE",
    PASSWORD_CHANGE_FAILED = "PASSWORD_CHANGE_FAILED",
    USER_DELETE_FAILED = "USER_DELETE_FAILED",
    SESSION_EXPIRED = "SESSION_EXPIRED",
    DELETED_TEMPLATE = "DELETED_TEMPLATE",
}

interface ISimpleToastDetail {
    intent: Intent;
    text: string;
}

export interface IEditLeaveData {
    mediaId: string;
    documentId?: BigIntId;
    documentRevisionId?: BigIntId;
    quickEditEntry: QuickEditEntry;
}

// /api/v1/admin/member/templates/:templateId/items/:itemId
const TEMPLATE_ITEMS_PATH_RE = /^\/api\/v1\/admin\/member\/templates\/[^/]+\/items\/[^/]+(?:\/|$)/;

// /api/v1/admin/member/templates と /api/v1/admin/member/templates/:templateId
const TEMPLATE_PATH_RE = /^\/api\/v1\/admin\/member\/templates(?:\/|$)/;

function friendlyMessageFromPath(path?: string): string | null {
    if (!path) return null;

    // 先に items を判定（より具体的な方を先）
    if (TEMPLATE_ITEMS_PATH_RE.test(path)) {
        return "指定したデータが見つかりません。";
    }
    if (TEMPLATE_PATH_RE.test(path)) {
        return "このテンプレートは削除されています。";
    }

    return null;
}

export class ToastStore {
    @observable
    public httpError: HttpError | null = null;

    @observable
    public simpleToast: SimpleToast | null = null;

    @observable
    public isEditLeaveShown: boolean = false;

    public editLeaveData: IEditLeaveData | null = null;

    @observable
    public isKeepFirstRouteChange: boolean = false;

    public pageRouter: PageRouter;

    constructor(pageRouter: PageRouter) {
        this.pageRouter = pageRouter;
        makeObservable(this);
    }

    @action.bound
    public routeChanged() {
        if (this.isKeepFirstRouteChange) {
            this.isKeepFirstRouteChange = false;
            return;
        }

        this.setHttpError(null);
        this.setSimpleToast(null);
    }

    @action.bound
    public setHttpError(httpError: HttpError | null) {
        this.httpError = httpError;
    }

    @computed
    public get httpErrorDetail(): IHttpErrorDetail | null {
        if (!this.httpError) {
            return null;
        }
        if (this.httpError.userMessage) {
            return {
                intent: Intent.WARNING,
                message: this.httpError.userMessage,
            };
        } else if (friendlyMessageFromPath(this.httpError.path)) {
            return {
                intent: Intent.DANGER,
                message: friendlyMessageFromPath(this.httpError.path)!,
            };
        } else if (this.httpError.message) {
            return {
                intent: Intent.DANGER,
                message: this.httpError.message,
            };
        } else {
            return {
                intent: Intent.DANGER,
                message: "エラーが発生しました。リロードしてください。",
            };
        }
    }

    @action.bound
    public setEditLeaveData(editLeaveData: IEditLeaveData | null) {
        this.isEditLeaveShown = !!editLeaveData;
        this.editLeaveData = editLeaveData;
    }

    @action.bound
    public hideEditLeave() {
        this.isEditLeaveShown = false;
    }

    @action.bound
    public backToEditLeave() {
        const editLeaveData = this.editLeaveData;
        if (!editLeaveData) {
            return;
        }

        if (editLeaveData.documentId) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.pageRouter.pushRoute("/admin/[mediaId]/documents/[documentId]", {
                mediaId: editLeaveData.mediaId,
                documentId: editLeaveData.documentId.value,
                editLeave: "true",
            });
        } else {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            this.pageRouter.pushRoute("/admin/[mediaId]/revisions/[documentRevisionId]", {
                mediaId: editLeaveData.mediaId,
                documentRevisionId: editLeaveData.documentRevisionId!.value,
                editLeave: "true",
            });
        }
    }

    @action.bound
    public setSimpleToast(simpleToast: SimpleToast | null) {
        this.simpleToast = simpleToast;
    }

    @computed
    public get simpleToastDetail(): ISimpleToastDetail | null {
        if (!this.simpleToast) {
            return null;
        }

        switch (this.simpleToast) {
            case SimpleToast.SAVED:
                return {
                    intent: Intent.SUCCESS,
                    text: "保存しました。",
                };
            case SimpleToast.DELETED:
                return {
                    intent: Intent.SUCCESS,
                    text: "削除しました。",
                };
            case SimpleToast.FINISHED_PUBLISH_SETTING:
                return {
                    intent: Intent.SUCCESS,
                    text: "公開設定が完了しました。",
                };
            case SimpleToast.FINISHED_ARCHIVED_SETTING:
                return {
                    intent: Intent.SUCCESS,
                    text: "アーカイブ状態を変更しました。",
                };
            case SimpleToast.CANNOT_OPEN_WINDOW:
                return {
                    intent: Intent.WARNING,
                    text: "新しいウィンドウが開けませんでした。",
                };
            case SimpleToast.SCHEDULED_REVISION_EXISTS:
                return {
                    intent: Intent.WARNING,
                    text: "予約公開が存在するため、公開できません。",
                };
            case SimpleToast.COMPLETED:
                return {
                    intent: Intent.SUCCESS,
                    text: "完了しました。",
                };
            case SimpleToast.PROFILE_UPDATED:
                return {
                    intent: Intent.SUCCESS,
                    text: "プロフィールが正常に更新されました。",
                };
            case SimpleToast.PROFILE_UPDATE_FAILED:
                return {
                    intent: Intent.DANGER,
                    text: "プロフィールの更新に失敗しました。",
                };
            case SimpleToast.PROFILE_UPDATE_SAM_FAILED:
                return {
                    intent: Intent.WARNING,
                    text: "変更がありません。",
                };
            case SimpleToast.PROFILE_FETCH_FAILED:
                return {
                    intent: Intent.DANGER,
                    text: "ユーザー情報の取得に失敗しました。",
                };
            case SimpleToast.PASSWORD_CHANGE_COMPLETE:
                return {
                    intent: Intent.SUCCESS,
                    text: "パスワードが正常に変更されました",
                };
            case SimpleToast.PASSWORD_CHANGE_FAILED:
                return {
                    intent: Intent.DANGER,
                    text: "パスワードの変更に失敗しました",
                };
            case SimpleToast.USER_DELETE_FAILED:
                return {
                    intent: Intent.DANGER,
                    text: "ユーザーの削除に失敗しました",
                };
            case SimpleToast.SESSION_EXPIRED:
                return {
                    intent: Intent.WARNING,
                    text: "セッションの有効期限が切れました。再度ログインしてください。",
                };
            case SimpleToast.DELETED_TEMPLATE:
                return {
                    intent: Intent.DANGER,
                    text: "このテンプレートは削除されています。",
                };
        }
    }

    @action.bound
    public keepFirstRouteChange() {
        this.isKeepFirstRouteChange = true;
    }
}
